/**
 * What each integration does with a lead, for the Add Integration list and the details view.
 * Written from the delivery code in stentor-api's integration-server and stentor-core's
 * stentor-service-* packages, not the vendors' marketing: keep it to what XAPP actually does.
 */
/**
 * What customers see Lead Connect called. The code, API and events keep "leadConnect"; only
 * this changes when the product is renamed.
 */
export const LEAD_CONNECT_NAME = "Call Connect";

export interface IntegrationCatalogEntry {
    name: string;
    /** One line: what happens to a new lead */
    summary: string;
    /** Short tags, most important first */
    capabilities: string[];
}

export const INTEGRATION_CATALOG: Record<string, IntegrationCatalogEntry> = {
    servicetitan: {
        name: "ServiceTitan",
        summary: "Books each lead into ServiceTitan as a job or booking, with your campaign and business unit.",
        capabilities: ["Creates jobs or bookings", "Live availability", "Lead import", "Job and invoice outcomes"],
    },
    jobber: {
        name: "Jobber",
        summary: "Creates the client, property and request in Jobber, and a job when the customer picked a time.",
        capabilities: ["Creates clients and requests", "Creates jobs", "Live availability"],
    },
    housecallpro: {
        name: "Housecall Pro",
        summary: "Creates the customer and lead in Housecall Pro, plus a job when the request is complete.",
        capabilities: ["Creates customers and leads", "Creates jobs", "Live availability"],
    },
    serviceminder: {
        name: "ServiceMinder",
        summary: "Adds each lead as a ServiceMinder customer with a note, and books the job when live availability is on.",
        capabilities: ["Creates customers", "Books jobs", "Live availability (optional)"],
    },
    fieldpulse: {
        name: "FieldPulse",
        summary: "Creates or updates the FieldPulse customer and opens a job for each lead.",
        capabilities: ["Creates customers", "Creates jobs"],
    },
    fieldroutes: {
        name: "FieldRoutes",
        summary: "Creates or updates the FieldRoutes customer, with an appointment when a date was requested.",
        capabilities: ["Creates customers", "Creates appointments"],
    },
    jobnimbus: {
        name: "JobNimbus",
        summary: "Creates or updates the JobNimbus contact, optionally a job, and adds a note, routed to the workflow you pick.",
        capabilities: ["Creates contacts", "Creates jobs", "Workflow routing"],
    },
    hubspot: {
        name: "HubSpot",
        summary: "Adds each lead as a HubSpot contact, and books a meeting from your scheduling link.",
        capabilities: ["Creates contacts", "Books meetings", "Live availability"],
    },
    lacrm: {
        name: "Less Annoying CRM",
        summary: "Adds each lead as a contact with the conversation as a note and a pipeline item.",
        capabilities: ["Creates contacts", "Adds pipeline items"],
    },
    surefire: {
        name: "Surefire Local",
        summary: "Sends each lead to Surefire Local.",
        capabilities: ["Sends leads"],
    },
    zapier: {
        name: "Zapier",
        summary: "Sends each lead to a Zap, so you can route it to thousands of other apps.",
        capabilities: ["Sends leads", "Connects other apps"],
    },
    webhook: {
        name: "Webhook",
        summary: "Posts each lead as JSON to your URL, nested or flat, with optional field mapping.",
        capabilities: ["Sends leads", "Custom JSON", "Field mapping"],
    },
    boberdoo: {
        name: "boberdoo",
        summary: "Posts each lead to your boberdoo lead portal.",
        capabilities: ["Sends leads"],
    },
    leadperfection: {
        name: "LeadPerfection",
        summary: "Posts each lead to LeadPerfection with your product mapping, in the business's local time.",
        capabilities: ["Sends leads", "Product mapping"],
    },
    free2grow: {
        name: "Free-2-Grow",
        summary: "Sends each lead to Free-2-Grow under this business's XAPP reference ID.",
        capabilities: ["Sends leads", "Reference ID"],
    },
    contractor_appointments: {
        name: "Contractor Appointments",
        summary: "Sends each lead to your Contractor Appointments campaign.",
        capabilities: ["Sends leads"],
    },
    facebook_capi: {
        name: "Facebook Conversions API",
        summary: "Reports each lead to Meta as a Lead conversion event, so your ads can optimize for it.",
        capabilities: ["Ad conversions"],
    },
    searchlight: {
        name: "SearchLight",
        summary: "Copies each analyzed lead to SearchLight's lead feed.",
        capabilities: ["Lead feed"],
    },
    leadconnect: {
        name: LEAD_CONNECT_NAME,
        summary: "Calls you from your own local number about each qualifying lead, and connects you to the homeowner when you press 1.",
        capabilities: ["Paid add-on", "Calls the business", "Live transfer", "Call outcomes"],
    },
};
